<script setup lang="ts">
/**
 * Site footer.
 *
 * The profile links are rendered from `identityConfig.profiles` — the same array that becomes the
 * entity's `sameAs` in JSON-LD and the `<link rel="me">` tags in the head. Rendering them from one
 * source is what keeps the visible links and the machine-readable claim in agreement; a `sameAs`
 * entry with no corresponding link on the page is an assertion with nothing behind it.
 *
 * Every other fact here comes from config for the same reason. Nothing in this component is typed
 * out twice, which is why a fork that edits `site.ts` and `identity.ts` never has to open it.
 */

/**
 * Vue
 */
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { ArrowUpRight } from '@lucide/vue'

/**
 * Components
 */
import AppContainer from '@/components/ui/AppContainer.vue'

/**
 * Config
 */
import { identityConfig } from '@/config/identity'
import { siteConfig } from '@/config/site'
import { softwareConfig } from '@/config/software'

/**
 * Utilities
 */
import { hostLabel } from '@/utils/hostLabel'

const { t } = useI18n()

const links = [
  { to: '/', key: 'nav.home' },
  { to: '/about', key: 'nav.about' },
  { to: '/author', key: 'nav.author' },
  { to: '/documentation', key: 'nav.documentation' },
  { to: '/contact', key: 'nav.contact' }
]
</script>

<template>
  <footer class="relative mt-24 border-t border-border">
    <!--
      A hairline of the accent gradient along the top edge. Two pixels of colour is enough to close
      the page; a filled footer band competes with the content above it.
    -->
    <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

    <AppContainer class="py-14">
      <div class="grid gap-10 md:grid-cols-[2fr_1fr_1fr]">
        <div>
          <div class="flex items-center gap-2.5">
            <span
              class="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-chart-2 font-headline text-sm font-bold text-primary-foreground"
              aria-hidden="true"
            >
              {{ siteConfig.shortName.slice(0, 2) }}
            </span>
            <span class="font-headline text-base font-bold tracking-tight">{{ siteConfig.name }}</span>
          </div>

          <!--
            The blurb under the wordmark describes the *site*, not the person who wrote it. The
            lockup above it is `siteConfig.shortName` and `siteConfig.name`, so a paragraph sourced
            from `identityConfig.description` would introduce the entity under someone else's name.
            The author is credited in the copyright line below, which is where the identity config
            belongs.
          -->
          <p class="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
            {{ siteConfig.description }}
          </p>
        </div>

        <nav :aria-label="siteConfig.name + ' footer'">
          <h2 class="font-headline text-sm font-semibold tracking-tight">{{ t('footer.sitemap') }}</h2>
          <ul class="mt-4 space-y-2.5 text-sm">
            <li v-for="link in links" :key="link.to">
              <router-link :to="link.to" class="text-muted-foreground transition hover:text-foreground">
                {{ t(link.key) }}
              </router-link>
            </li>
          </ul>
        </nav>

        <nav :aria-label="siteConfig.name + ' profiles'">
          <h2 class="font-headline text-sm font-semibold tracking-tight">{{ t('footer.elsewhere') }}</h2>
          <ul class="mt-4 space-y-2.5 text-sm">
            <li v-for="profile in identityConfig.profiles" :key="profile">
              <a
                :href="profile"
                rel="me noreferrer"
                target="_blank"
                class="group inline-flex items-center gap-1 text-muted-foreground transition hover:text-foreground"
              >
                {{ hostLabel(profile) }}
                <ArrowUpRight
                  class="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <div
        class="mt-12 flex flex-col gap-3 border-t border-border pt-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between"
      >
        <!--
          The name links to the entity's own site, which is the same URL `identityConfig.canonicalId`
          anchors the graph on — so the visible credit and the machine-readable one point at one
          place. `rel="me"` for that reason: it is the HTML half of the claim the JSON-LD makes.

          `noopener` without `noreferrer`, unlike the profile links above. Those go to third parties,
          where withholding the referrer is a privacy choice; this one goes to the entity's own site,
          which has every reason to see where its traffic came from.

          The new-tab warning is screen-reader-only because the behaviour is already visible to a
          sighted user from the cursor and the browser's own affordances, while an unannounced tab
          change is disorienting to anyone who cannot see it happen.
        -->
        <p>
          &copy; {{ new Date().getFullYear() }}
          <a
            v-if="identityConfig.url"
            :href="identityConfig.url"
            target="_blank"
            rel="me noopener"
            class="font-medium text-foreground underline-offset-4 transition hover:text-primary hover:underline"
            >{{ identityConfig.name }}<span class="sr-only"> ({{ t('actions.newTab') }})</span></a
          ><span v-else>{{ identityConfig.name }}</span
          >. {{ t('footer.license') }}
        </p>

        <p class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>{{ t('footer.builtWith') }}</span>
          <span v-if="softwareConfig.enabled" class="font-mono text-xs text-muted-foreground/80">
            v{{ softwareConfig.version }}
          </span>
        </p>
      </div>
    </AppContainer>
  </footer>
</template>
